const modalities = [
  {
    eyebrow: "01 — Trauma-Informed Counselling",
    title:
      "Past experiences shape how we think, feel, relate, and respond to the world.",
    body: "These patterns are often adaptive responses that once helped us cope, even if they no longer serve us today. Therapy begins by creating a foundation of safety, trust, and collaboration. Working at a pace that feels manageable, we gently explore these patterns while developing greater self-awareness, emotional regulation, and the capacity for meaningful, lasting change.",
    testId: "modality-trauma",
  },
  {
    eyebrow: "02 — Parts Work",
    title:
      "The mind is made up of different parts — each with its own emotions and protective strategies.",
    body: "Rather than fighting these parts, therapy helps cultivate curiosity, compassion, and greater internal balance. Over time, this creates a more harmonious relationship with yourself and a steadier base from which to meet the world.",
    testId: "modality-ifs",
  },
  {
    eyebrow: "03 — Polyvagal Theory & Nervous System Regulation",
    title:
      "A practical understanding of how the nervous system responds to safety, stress, and connection.",
    body: "By becoming more aware of these patterns, you can learn to regulate your emotional responses more effectively, build resilience, and navigate life’s challenges and relationships with greater confidence, flexibility, and ease.",
    testId: "modality-polyvagal",
  },
  {
    eyebrow: "04 — Mindfulness & Somatic Awareness",
    title:
      "The body is an important source of awareness — not just sensation.",
    body: "Learning to safely slow down, notice, and listen to your body’s signals can reveal deeper insight into your emotions, patterns, and unmet needs, while supporting nervous system regulation and resilience. Sessions may include grounding, mindfulness, breath awareness, and gentle body-based practices.",
    testId: "modality-mindfulness",
  },
  {
    eyebrow: "05 — Psychedelic Integration Support",
    title:
      "Grounded support for integrating expanded states of consciousness.",
    body: "Integration is the process of making sense of these experiences and translating their insights into meaningful, sustainable changes in everyday life. Together, we explore how these experiences can be understood, embodied, and integrated in ways that support your wellbeing, relationships, and personal growth.",
    testId: "modality-integration",
  },
];

const Approach = () => {
  return (
    <div data-testid="approach-page">
      {/* Intro */}
      <section
        className="border-b"
        style={{
          borderColor: "rgba(201, 192, 171, 0.55)",
          backgroundColor: "var(--mc-bg)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <p
            className="text-sm md:text-base tracking-eyebrow uppercase mb-6"
            style={{ color: "var(--mc-muted)" }}
          >
            Therapeutic Approach
          </p>
          <h1
            className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] max-w-4xl"
            style={{ color: "var(--mc-ink)" }}
            data-testid="approach-heading"
          >
            There is no one-size-fits-all approach to therapy.
          </h1>
          <p
            className="mt-10 text-lg md:text-xl leading-relaxed max-w-3xl font-light"
            style={{ color: "var(--mc-ink-2)" }}
            data-testid="approach-intro"
          >
            Every person brings a unique history, set of challenges, and way of
            experiencing the world. My role is to understand your individual
            needs and tailor therapy to support you best.
          </p>
        </div>
      </section>

      {/* Plain-language note */}
      <section
        style={{ backgroundColor: "var(--mc-bg-3)" }}
        data-testid="approach-simple-note"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2
            className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-8"
            style={{ color: "var(--mc-ink)" }}
          >
            Sometimes we simply need someone to talk to.
          </h2>
          <p
            className="text-lg leading-relaxed max-w-3xl font-light mb-6"
            style={{ color: "var(--mc-ink-2)" }}
          >
            While I draw on a range of therapeutic approaches, counselling
            and therapy shouldn’t feel complex.
          </p>
          <p
            className="text-lg leading-relaxed max-w-3xl font-light"
            style={{ color: "var(--mc-ink-2)" }}
          >
            Many people come to therapy because they’re feeling overwhelmed,
            stuck, uncertain, or carrying more than they can manage alone.
            Sometimes what helps most is a safe, supportive space to slow down,
            speak openly, and feel genuinely heard. You don’t need to be in
            crisis, have experienced major trauma, or know exactly what you want
            to work on. We can begin wherever you are.
          </p>
        </div>
      </section>

      {/* Modalities list */}
      <section
        className="border-t"
        style={{
          borderColor: "rgba(201, 192, 171, 0.55)",
          backgroundColor: "var(--mc-bg)",
        }}
        data-testid="modalities-section"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 space-y-24 md:space-y-32">
          {modalities.map((m, idx) => (
            <article
              key={m.testId}
              className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16"
              data-testid={m.testId}
            >
              <div className="md:col-span-4">
                <p
                  className="text-sm md:text-base tracking-eyebrow uppercase"
                  style={{ color: "var(--mc-muted)" }}
                >
                  {m.eyebrow}
                </p>
              </div>
              <div className="md:col-span-8">
                <h3
                  className="font-serif-display text-2xl md:text-4xl leading-snug mb-6"
                  style={{ color: "var(--mc-ink)" }}
                >
                  {m.title}
                </h3>
                <p
                  className="text-base md:text-lg leading-relaxed font-light"
                  style={{ color: "var(--mc-ink-2)" }}
                >
                  {m.body}
                </p>
              </div>
              {idx !== modalities.length - 1 && (
                <div
                  className="md:col-span-12 h-px"
                  style={{ backgroundColor: "var(--mc-border)" }}
                />
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Closing CTA removed per user request */}
    </div>
  );
};

export default Approach;
